import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { environment } from '@env/environment';
import { User } from '@shared/models';
import { deserialize } from '@shared/models/base-helper';
import { UserActivity } from '@shared/models/user-activity.model';
import * as fromApp from '@store';
import { SpinnerService } from '@shared/spinner/spinner.service';
import { MatDialog } from '@angular/material/dialog';
import { DialogYesNoComponent } from '@shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { selectIsMobileView } from '@store/app-config/app-config.selectors';
import { AppConfigService } from '@core/api/app-config.service';
import { ToastLevel } from '@shared/models/toast-message.module';
import { ToastService } from '@shared/services/toast.service';

interface QueryResponse {
  results: any[];
  total: number;
}

interface ExistsResponse {
  exists: boolean;
}

interface DeserializedResponse {
  results: User[];
  total: number;
}

interface ActivityQueryResponse {
  results: any[];
  total: number;
}

interface DeserializedActivityResponse {
  results: UserActivity[];
  total: number;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  apiUrl = environment.apiUrl + '/users/';

  constructor(
    readonly http: HttpClient, readonly store: Store<fromApp.AppState>,
    readonly appConfigService: AppConfigService,
    readonly toastService: ToastService,
    public dialog: MatDialog,
    readonly spinnerService: SpinnerService
  ) {}

  isValid(token: string): Observable<string> {
    const headers = new HttpHeaders()
      .set('Authorization', `Bearer ${token}`)
      .set('Content-Type', 'application/json')
      .set('X-FC-Noauth', 'true');

    return this.http.get<any>(`${this.apiUrl}valid`, { headers }).pipe(
      map(resp => resp),
      catchError(error => of(error))
    );
  }

  getMe(): Observable<User> {
    return this.http.get<any>(`${this.apiUrl}me`).pipe(
      map((serializedUser: any) => deserialize(serializedUser, User)),
      catchError(() => of(null))
    );
  }

  getAdmins(): Observable<User[]> {
    return this.http.get<any[]>(`${this.apiUrl}admins`).pipe(
      map((response: any[]) => response.map((serializedUser: any) => deserialize(serializedUser, User))),
      catchError(error => of(error))
    );
  }

  getUsers(): Observable<User[]> {
    return this.http.get<QueryResponse>(this.apiUrl).pipe(
      map(response => {
        const results = [];
        for (const serializedUser of response.results) {
          results.push(deserialize(serializedUser, User));
        }
        return results;
      })
    );
  }

  getUsersForStatDashboard(): Observable<User[]> {
    return this.http.get<any[]>(`${this.apiUrl}stat_dashboard/`).pipe(
      map((response: any) => deserialize(response, User))
    );
  }

  exists(email: string): Observable<boolean> {
    return this.http
      .get<ExistsResponse>(`${this.apiUrl}exists`, {
        params: new HttpParams().set('email', encodeURIComponent(email.toLowerCase())),
        headers: new HttpHeaders().set('X-FC-Noauth', 'true')
      })
      .pipe(
        map((response: ExistsResponse) => {
          return response.exists;
        })
      );
  }

  getUser(id: number): Observable<User> {
    return this.http.get<any>(`${this.apiUrl}${id}`).pipe(map(serializedUser => deserialize(serializedUser, User)));
  }

  deleteUser(userId: number) {
    return this.http.delete<boolean>(`${this.apiUrl}${userId}/`).pipe(map((response: any) => response.success));
  }

  getFilteredUsers(filterString: string): Observable<DeserializedResponse> {
    const filterUrl = this.apiUrl + filterString;

    return this.http.get<QueryResponse>(filterUrl).pipe(
      map(response => {
        const deserializedResponse: DeserializedResponse = {
          results: [],
          total: response.total,
        };
        for (const serializedUser of response.results) {
          deserializedResponse.results.push(deserialize(serializedUser, User));
        }
        return deserializedResponse;
      })
    );
  }

  requestUserOpen(body): Observable<User> {

    return this.http
      .post<any>(`${this.apiUrl}request`, body, { headers: new HttpHeaders().set('X-FC-Noauth', 'true') })
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }

  updateUserProfile(profile: any): Observable<User> {
    return this.http
      .put<any>(`${this.apiUrl}${profile.id}/profile`, profile)
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }

  inviteUpdateUser(
    email: string,
    role: string,
    isActive: boolean,
    id?: number
  ): Observable<User> {
    const lowerEmail = email.toLowerCase();
    const body = { email: lowerEmail, role, is_active: isActive, id };
    if (id) {
      return this.http
        .patch<any>(`${this.apiUrl}update_user_permissions/${id}`, body)
        .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
    } else {
      return this.http
        .post<any>(`${this.apiUrl}invite`, body)
        .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
    }
  }

  sendPasswordChangeEmail() {
    return this.http
      .post<string>(`${this.apiUrl}notify`, {}, { params: new HttpParams().set('subject', 'password-change') })
      .pipe(map((response: string) => response));
  }

  createDelegate(data: any): Observable<User> {
    return this.http
      .post<any>(`${this.apiUrl}delegate`, data)
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }

  removeDelegate(): Observable<User> {
    return this.http
      .delete<any>(`${this.apiUrl}delegate`)
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }

  verifyUser(userId: number, verified: boolean, clear: boolean): Observable<User> {
    return this.http
      .put<any>(`${this.apiUrl}${userId}/verify`, {
        verified,
        clear,
      })
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }


  logUserActivity(data: any) {
    this.http
      .put<any>(`${this.apiUrl}log_activity`, data).subscribe({
      error: (err) => this.toastService.sendMessage("Error Logging Activity", ToastLevel.WARN)
    });
  }

  changeEmailAddress(userId: number, email: string): Observable<User> {
    const lowerEmail = email.toLowerCase();
    return this.http
      .put<any>(`${this.apiUrl}${userId}/email`, {
        email: lowerEmail,
      })
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }

  resendInvite(userIds: number[]) {
    return this.http
      .post<string>(`${this.apiUrl}reinvite`, userIds)
      .pipe(map((response: string) => response));
  }

  adminResetPassword(userId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl}reset_password?user_id=${userId}`).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getStripeAccountCreationToken(userId: number): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}${userId}/stripe-token`, {})
      .pipe(map((response: string) => response));
  }

  doStripeConnect(userId: number) {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Notice',
          line1: `You are leaving LandYield and going to our third-party payment provider, Stripe.`,
          line2: `Please ensure that the entity you use to create your account matches your tax entity preferences.`,
          buttonTextYes: 'Continue',
          ...this.appConfigService.getPopupDetails('stripe-connect')
        },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe(result => {
        this.spinnerService.show('app-spinner', 'Redirecting to Stripe');
        this.getStripeAccountCreationToken(userId).subscribe({
          next: (result: any) => {
            this.store
              .select(selectIsMobileView)
              .pipe(take(1))
              .subscribe(mobileViewMode => {
                if (mobileViewMode) {
                  window.location.href = result
                } else {
                  window.open(result, '_blank', 'noopener,noreferrer');
                  this.spinnerService.hide('app-spinner');
                }
              });
          },
          error: error => {
            this.spinnerService.hide('app-spinner');
            console.log('Error getting Stripe token', error);
            return throwError(() => new Error(error));
          }
        });
      });
  }

  allowReset(email: string) {
    return this.http
      .get<boolean>(`${this.apiUrl}can_reset`, {
        params: new HttpParams().set('email', encodeURIComponent(email.toLowerCase())),
        headers: new HttpHeaders().set('X-FC-Noauth', 'true')
      })
      .pipe(map((response: any) => response.allow_reset));
  }

  getUserActivity(filterString: string): Observable<DeserializedActivityResponse> {
    return this.http.get<ActivityQueryResponse>(`${this.apiUrl}activity/` + filterString).pipe(
      map(response => {
        const deserializedResponse: DeserializedActivityResponse = {
          results: [],
          total: response.total,
        };
        for (const activity of response.results) {
          deserializedResponse.results.push(deserialize(activity, UserActivity));
        }
        return deserializedResponse;
      })
    );
  }

  refreshStripeAccount(userId: number) {
    return this.http
      .get<User>(`${this.apiUrl}${userId}/refresh_stripe`)
      .pipe(map((serializedUser: any) => deserialize(serializedUser, User)));
  }

  beginSmsVerification() {
    return this.http
      .get<string>(`${this.apiUrl}sms_verification/`)
      .pipe(map((response: string) => response));
  }

  checkSmsVerification(otp: string): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.apiUrl}sms_verification/`, { otp })
      .pipe(map((response: boolean) => response));
  }

}
